import React, { FunctionComponent, useState } from "react";
import { TimeoutDuration } from "../../enums.generated";
import { Message } from "../../models/app";
import { Guid } from "../../util/Guid";
import { Toast } from "./Toast";

/**
 * Message list interface, used to configure a list of messages.
 */
export interface MessageListOptions {
	messages: Array<Message>;
	addMessage?: (message: Message) => void;
	clearMessages?: () => void;
}

/**
 *Message list state interface.
 */
interface MessageListState {	
	messages: Array<Message>;
}

/**
 * Message list component, displays one or many messages as Boostrap toasts.
 * @param props
 */
export const MessageList: FunctionComponent<{ options: MessageListOptions }> = (props: { options: MessageListOptions }) => {

	// setup state
	const [state, setState] = useState({
		messages: props.options.messages
	} as MessageListState);

	// refresh state hander
	const refreshState = () => {

		setState({ ...state });
	};

	// add message handler
	const addMessage = (message: Message) => {

		const match = state.messages.find(m => m.title === message.title && m.text === message.text);
		if (!match) {
			state.messages.push(message);
			refreshState();

			// if there's a timeout then hide the message once the duration has passed
			if (message.timeoutDuration) {

				let duration: number | undefined;

				switch (message.timeoutDuration) {
					case TimeoutDuration.Fast:
						duration = 7500;
						break;
					case TimeoutDuration.Standard:
						duration = 15000;
						break;
					case TimeoutDuration.Slow:
						duration = 30000;
						break;
				}

				message.timeout = global.setTimeout(() => {
					dismissMessage(message);
				}, duration);
			}
		}
	};

	// dismiss message handler
	const dismissMessage = (message: Message) => {

		const index = state.messages.findIndex(m => m.id === message.id);
		state.messages.splice(index, 1);

		if (message.timeout) {
			clearTimeout(message.timeout);
		}

		refreshState();
	};

	// clear messages handler
	const clearMessages = () => {

		// dismiss each message to ensure any timeouts are cleared
		state.messages.forEach(message => {
			dismissMessage(message);
		});
		state.messages = [];
		refreshState();
	};

	props.options.addMessage = addMessage;
	props.options.clearMessages = clearMessages;

	// the toast elements to display
	const messageElements = state.messages.map(message => {

		message.dismiss = dismissMessage;

		if (!message.id) {

			message.id = Guid.newGuid();
		}

		return <Toast key={message.id} options={message} />;
	});

	// output the toasts wrapped within a div
	return (
		<div>
			{messageElements}
		</div>
	)
}